import React, { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { MydawaLogo } from "@/components/MydawaLogo";
import { Send, Phone, Loader2, Info } from "lucide-react";
import { FaWhatsapp } from "react-icons/fa";

const DEFAULT_PHONE = "+254712000001"; // Ruth Wanjiru (seeded)

export default function WhatsAppSimulator() {
  const [params, setParams] = useSearchParams();
  const [phone, setPhone] = useState(params.get("phone") || DEFAULT_PHONE);
  const [input, setInput] = useState("");
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [err, setErr] = useState("");
  const scrollRef = useRef(null);

  const load = async () => {
    setLoading(true);
    setErr("");
    try {
      const { data } = await api.get(`/wa/messages/${encodeURIComponent(phone)}`);
      setMessages(data);
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || "Unable to load chat");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [phone]);
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages]);

  const send = async (text) => {
    const body = (text ?? input).trim();
    if (!body) return;
    setSending(true);
    setInput("");
    // Optimistic inbound
    const tempId = `temp-${Date.now()}`;
    setMessages((m) => [...m, { id: tempId, recipient: phone, body, direction: "inbound", channel: "whatsapp", created_at: new Date().toISOString() }]);
    try {
      await api.post("/wa/inbound", { phone, text: body });
      await load();
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  const changePhone = (p) => {
    setPhone(p);
    setParams({ phone: p });
  };

  return (
    <div className="min-h-screen bg-[#e5ddd5]">
      <header className="border-b border-[#d1cec6] bg-[#075e54] text-white">
        <div className="mx-auto flex max-w-4xl items-center justify-between px-4 py-2">
          <div className="flex items-center gap-3">
            <div className="rounded-full bg-white p-1">
              <MydawaLogo className="h-6" />
            </div>
            <div>
              <div className="font-bold leading-tight">MYDAWA Refer &amp; Earn</div>
              <div className="text-[10px] opacity-90">WhatsApp Bot · online (simulated)</div>
            </div>
          </div>
          <a href="/admin/login" className="text-xs font-semibold opacity-90 hover:opacity-100" data-testid="sim-admin-link">Admin →</a>
        </div>
      </header>

      <div className="mx-auto max-w-4xl px-3 py-4">
        <div className="mb-3 flex flex-wrap items-center gap-2 rounded-xl border border-[#d1cec6] bg-white px-3 py-2 text-xs">
          <Phone className="h-3.5 w-3.5 text-[#075e54]" />
          <span className="font-semibold text-[#075e54]">Simulating referrer:</span>
          <select
            value={phone}
            onChange={(e) => changePhone(e.target.value)}
            className="rounded-md border border-[#d1cec6] bg-white px-2 py-1 text-xs"
            data-testid="sim-phone-select"
          >
            <option value="+254712000001">Ruth Wanjiru · +254712000001</option>
            <option value="+254712000002">Brian Otieno · +254712000002</option>
            <option value="+254712000003">Amina Hassan · +254712000003</option>
          </select>
          <span className="ml-auto text-[10px] text-[#5c665a]">Type commands: STATS · LINK · PAYOUTS · LEADERBOARD · HELP · STOP · or ask anything</span>
        </div>

        {err && (
          <div className="mb-3 rounded-lg bg-[#fff0f5] px-3 py-2 text-sm text-[#c01974]" data-testid="sim-error">{err}</div>
        )}

        <div
          ref={scrollRef}
          className="h-[58vh] overflow-y-auto rounded-xl border border-[#d1cec6] bg-[url('https://static.prod-images.emergentagent.com/jobs/f016b904-7317-4dc9-bed9-f4b71ecc7608/images/4458f48ce5474757c889fe64de0b2d3f806beff7486cfdcc4a1eb7dac2fd6c53.png')] bg-center bg-no-repeat p-3"
          style={{ backgroundColor: "#ece5dd" }}
          data-testid="sim-messages"
        >
          {loading ? (
            <div className="flex items-center justify-center py-6 text-[#5c665a]">
              <Loader2 className="h-5 w-5 animate-spin" />
            </div>
          ) : messages.length === 0 ? (
            <div className="flex h-full flex-col items-center justify-center gap-3 rounded-xl bg-white/70 p-6 text-center">
              <FaWhatsapp className="h-10 w-10 text-[#25d366]" />
              <div className="text-sm text-[#5c665a]">
                No messages yet. The MYDAWA bot delivers invites automatically after orders.
              </div>
              <div className="text-xs text-[#5c665a]">
                Try typing <code className="rounded bg-[#eaf8de] px-1">HELP</code> below to see what the bot can do, or head to admin and tap <strong>Send invite</strong>.
              </div>
            </div>
          ) : (
            messages.map((m) => <Bubble key={m.id} m={m} onAction={send} />)
          )}
        </div>

        <form
          onSubmit={(e) => { e.preventDefault(); send(); }}
          className="mt-3 flex items-center gap-2 rounded-full border border-[#d1cec6] bg-white px-3 py-2"
          data-testid="sim-input-form"
        >
          <input
            type="text"
            placeholder="Type a message"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            className="flex-1 bg-transparent text-sm outline-none"
            data-testid="sim-input"
          />
          <button
            type="submit"
            disabled={sending || !input.trim()}
            className="rounded-full bg-[#25d366] p-2 text-white disabled:opacity-50"
            data-testid="sim-send-btn"
          >
            {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
          </button>
        </form>

        <div className="mt-4 flex items-start gap-2 rounded-xl border border-[#d1cec6] bg-white px-3 py-2 text-xs text-[#5c665a]">
          <Info className="mt-0.5 h-3.5 w-3.5 text-[#075e54]" />
          <div>
            This is a <strong>WhatsApp simulator</strong>. In production, messages are delivered via WhatsApp Business API.
            There is no referrer web dashboard — every interaction lives in WhatsApp.
          </div>
        </div>
      </div>
    </div>
  );
}

function Bubble({ m, onAction }) {
  const isInbound = m.direction === "inbound";
  const isSms = m.channel === "sms";
  const time = new Date(m.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  // detect action chips like [Share link]  [See my earnings]  [Not now]
  const actionRegex = /\[([^\]]+)\]/g;
  const actions = [];
  let match;
  while ((match = actionRegex.exec(m.body)) !== null) actions.push(match[1]);
  const cleanBody = m.body.replace(/\[[^\]]+\]/g, "").trim();
  return (
    <div className={`mb-2 flex ${isInbound ? "justify-end" : "justify-start"}`}>
      <div
        className={`max-w-[85%] rounded-2xl px-3 py-2 text-sm shadow-sm whitespace-pre-wrap ${
          isInbound
            ? "bg-[#dcf8c6] text-[#1a1c19]"
            : isSms
              ? "bg-[#fff0d6] border border-[#e9c86a] text-[#1a1c19]"
              : "bg-white text-[#1a1c19]"
        }`}
      >
        {isSms && <div className="mb-1 text-[10px] font-bold uppercase tracking-widest text-[#a87700]">SMS · M-Pesa</div>}
        <div>{cleanBody}</div>
        {actions.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-1">
            {actions.map((a) => (
              <button
                key={a}
                onClick={() => {
                  if (/share link/i.test(a)) {
                    const link = (m.body.match(/https?:\/\/\S+/) || [])[0];
                    if (link) navigator.clipboard.writeText(link);
                    onAction?.("LINK");
                  } else if (/earnings/i.test(a)) onAction?.("STATS");
                  else if (/not now/i.test(a)) {}
                  else onAction?.(a.toUpperCase());
                }}
                className="rounded-full border border-[#25d366] bg-white px-3 py-1 text-[11px] font-semibold text-[#075e54] hover:bg-[#dcf8c6]"
                data-testid={`chip-${a.replace(/\s/g, "-").toLowerCase()}`}
              >
                {a}
              </button>
            ))}
          </div>
        )}
        <div className="mt-1 text-right text-[10px] text-[#5c665a]">{time}</div>
      </div>
    </div>
  );
}
